import React from 'react';
import { BarChart3, Loader2, Printer } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const colors = {
    owed: 'bg-red-100 text-red-700', pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700',
    not_reimbursed: 'bg-gray-100 text-gray-600', reimbursed: 'bg-green-100 text-green-700',
    submitted: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
};

const FinanceReportsTab = ({ ctx }) => {
  const {
    reportType, setReportType, reportYear, setReportYear,
    reportMonth, setReportMonth, reportData, setReportData,
    reportLoading, fetchReport
  } = ctx;

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between print:hidden">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={reportType} onChange={(e) => { setReportType(e.target.value); setReportData(null); }}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-medium" data-testid="report-type-select">
            <option value="monthly">Monthly Report</option>
            <option value="annual">Annual Report</option>
          </select>
          {reportType === 'monthly' && (
            <select value={reportMonth} onChange={(e) => setReportMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="report-month-select">
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          )}
          <select value={reportYear} onChange={(e) => setReportYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="report-year-select">
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={fetchReport} disabled={reportLoading}
            className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="generate-report-btn">
            {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Generate
          </button>
        </div>
        {reportData && (
          <button onClick={() => window.print()}
            className="flex items-center gap-1 px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]" data-testid="print-report-btn">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        )}
      </div>

      {reportLoading && (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" /></div>
      )}

      {reportData && !reportLoading && (
        <div className="report-printable bg-white border border-[#E5E7EB] rounded-lg" data-testid="report-content">
          <div className="p-8 border-b border-[#E5E7EB] text-center">
            <h2 className="text-2xl font-bold text-[#0A0A0A] tracking-wide">{reportData.organization}</h2>
            <div className="w-16 h-0.5 bg-[#BA1826] mx-auto my-3"></div>
            <h3 className="text-lg font-semibold text-[#4B5563]">{reportData.period}</h3>
            <p className="text-xs text-[#9CA3AF] mt-2">Generated: {new Date(reportData.generated_at).toLocaleString()}</p>
          </div>

          <div className="p-8">
            <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Financial Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="border border-[#E5E7EB] rounded p-4">
                <p className="text-xs text-[#4B5563] mb-1">Total Expenses</p>
                <p className="text-xl font-bold text-[#0A0A0A]">${reportData.expenses.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                <p className="text-xs text-[#9CA3AF]">{reportData.expenses.count} transactions</p>
              </div>
              <div className="border border-[#E5E7EB] rounded p-4">
                <p className="text-xs text-[#4B5563] mb-1">Reimb. Outstanding</p>
                <p className="text-xl font-bold text-red-600">${reportData.reimbursements.outstanding_owed.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                <p className="text-xs text-[#9CA3AF]">Owed + Pending</p>
              </div>
              <div className="border border-[#E5E7EB] rounded p-4">
                <p className="text-xs text-[#4B5563] mb-1">Reimb. Paid (All Time)</p>
                <p className="text-xl font-bold text-green-600">${reportData.reimbursements.total_paid_all_time.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              </div>
              <div className="border border-[#E5E7EB] rounded p-4">
                <p className="text-xs text-[#4B5563] mb-1">Trips</p>
                <p className="text-xl font-bold text-[#0A0A0A]">{reportData.trips.count}</p>
                <p className="text-xs text-[#9CA3AF]">{reportData.trips.total_miles} miles</p>
              </div>
            </div>

            {Object.keys(reportData.expenses.by_category).length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Expenses by Category</h4>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Category</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Amount</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">% of Total</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(reportData.expenses.by_category).map(([cat, amt]) => (
                      <tr key={cat} className="border-b border-[#F1F5F9]">
                        <td className="py-2">{cat}</td>
                        <td className="py-2 text-right font-medium">${amt.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className="py-2 text-right text-[#4B5563]">{reportData.expenses.total > 0 ? ((amt / reportData.expenses.total) * 100).toFixed(1) : 0}%</td>
                      </tr>
                    ))}
                    <tr className="font-bold border-t-2 border-[#0A0A0A]">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-right">${reportData.expenses.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      <td className="py-2 text-right">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {Object.keys(reportData.expenses.by_payment_method).length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Expenses by Payment Method</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(reportData.expenses.by_payment_method).map(([method, amt]) => (
                    <div key={method} className="border border-[#E5E7EB] rounded px-3 py-2">
                      <p className="text-xs text-[#4B5563]">{method}</p>
                      <p className="text-sm font-semibold">${amt.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(reportData.expenses.by_funding_source).length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Funding Sources</h4>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Source</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Amount</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(reportData.expenses.by_funding_source).map(([src, amt]) => (
                      <tr key={src} className="border-b border-[#F1F5F9]">
                        <td className="py-2">{src}</td>
                        <td className="py-2 text-right font-medium">${amt.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {reportData.period_type === 'annual' && reportData.monthly_breakdown && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Monthly Breakdown</h4>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Month</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Expenses</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]"># Trans.</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Trips</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Miles</th>
                  </tr></thead>
                  <tbody>
                    {reportData.monthly_breakdown.map((m) => (
                      <tr key={m.month} className={`border-b border-[#F1F5F9] ${m.expense_total === 0 && m.trip_count === 0 ? 'text-[#9CA3AF]' : ''}`}>
                        <td className="py-2">{m.month}</td>
                        <td className="py-2 text-right font-medium">${m.expense_total.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className="py-2 text-right">{m.expense_count}</td>
                        <td className="py-2 text-right">{m.trip_count}</td>
                        <td className="py-2 text-right">{m.trip_miles}</td>
                      </tr>
                    ))}
                    <tr className="font-bold border-t-2 border-[#0A0A0A]">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-right">${reportData.expenses.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      <td className="py-2 text-right">{reportData.expenses.count}</td>
                      <td className="py-2 text-right">{reportData.trips.count}</td>
                      <td className="py-2 text-right">{reportData.trips.total_miles}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="mb-8">
              <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Reimbursement Status</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="border border-[#E5E7EB] rounded p-4">
                  <p className="text-xs text-[#4B5563] mb-1">Outstanding (Owed + Pending)</p>
                  <p className="text-lg font-bold text-red-600">${reportData.reimbursements.outstanding_owed.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div className="border border-[#E5E7EB] rounded p-4">
                  <p className="text-xs text-[#4B5563] mb-1">Approved (Awaiting Payment)</p>
                  <p className="text-lg font-bold text-blue-600">${reportData.reimbursements.outstanding_approved.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div className="border border-[#E5E7EB] rounded p-4">
                  <p className="text-xs text-[#4B5563] mb-1">Paid (All Time)</p>
                  <p className="text-lg font-bold text-green-600">${reportData.reimbursements.total_paid_all_time.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
              </div>
              {reportData.period_type === 'monthly' && reportData.reimbursements.period_items?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-[#4B5563] mb-2">Reimbursements This Period ({reportData.reimbursements.period_count})</p>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-2 text-xs font-semibold text-[#4B5563]">Person</th>
                      <th className="text-right py-2 text-xs font-semibold text-[#4B5563]">Amount</th>
                      <th className="text-center py-2 text-xs font-semibold text-[#4B5563]">Status</th>
                      <th className="text-left py-2 text-xs font-semibold text-[#4B5563]">Notes</th>
                    </tr></thead>
                    <tbody>
                      {reportData.reimbursements.period_items.map((r) => (
                        <tr key={r.id} className="border-b border-[#F1F5F9]">
                          <td className="py-2">{r.person_name}</td>
                          <td className="py-2 text-right font-medium">${r.total_amount.toFixed(2)}</td>
                          <td className="py-2 text-center"><StatusBadge status={r.status} /></td>
                          <td className="py-2 text-xs text-[#4B5563]">{r.payment_notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {reportData.trips.items?.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Trip Activity ({reportData.trips.count} trips, {reportData.trips.total_miles} miles)</h4>
                {reportData.trips.items.map((t) => (
                  <div key={t.id} className="border border-[#E5E7EB] rounded p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold">{t.destination}</span>
                        <span className="text-xs text-[#9CA3AF]">{t.date}</span>
                        {t.miles_traveled > 0 && <span className="text-xs bg-[#F1F5F9] px-2 py-0.5 rounded">{t.miles_traveled} mi</span>}
                      </div>
                      {t.reimbursement_owed && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Reimb. Owed</span>}
                    </div>
                    <p className="text-sm text-[#374151] mb-1"><span className="font-medium">Purpose:</span> {t.purpose}</p>
                    {t.people_involved?.length > 0 && <p className="text-xs text-[#4B5563]"><span className="font-medium">People:</span> {t.people_involved.join(', ')}</p>}
                    {t.meeting_notes && <p className="text-xs text-[#4B5563] mt-1"><span className="font-medium">Meeting Notes:</span> {t.meeting_notes}</p>}
                    {t.needs_identified && <p className="text-xs text-[#4B5563] mt-1"><span className="font-medium">Needs Identified:</span> {t.needs_identified}</p>}
                    {t.mission_support && <p className="text-xs text-[#4B5563] mt-1"><span className="font-medium">Mission Support:</span> {t.mission_support}</p>}
                  </div>
                ))}
              </div>
            )}

            {reportData.team_submissions.total > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Team Submissions</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-[#E5E7EB] rounded px-3 py-2"><p className="text-xs text-[#4B5563]">Total</p><p className="text-lg font-semibold">{reportData.team_submissions.total}</p></div>
                  <div className="border border-[#E5E7EB] rounded px-3 py-2"><p className="text-xs text-[#4B5563]">Approved</p><p className="text-lg font-semibold text-green-600">{reportData.team_submissions.approved}</p></div>
                  <div className="border border-[#E5E7EB] rounded px-3 py-2"><p className="text-xs text-[#4B5563]">Pending</p><p className="text-lg font-semibold text-yellow-600">{reportData.team_submissions.pending}</p></div>
                  <div className="border border-[#E5E7EB] rounded px-3 py-2"><p className="text-xs text-[#4B5563]">Rejected</p><p className="text-lg font-semibold text-red-600">{reportData.team_submissions.rejected}</p></div>
                </div>
              </div>
            )}

            {reportData.period_type === 'monthly' && reportData.expenses.items?.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Expense Detail</h4>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Date</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Paid By</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Category</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Description</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Amount</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Method</th>
                    <th className="text-center py-2 font-semibold text-[#4B5563]">Receipt</th>
                  </tr></thead>
                  <tbody>
                    {reportData.expenses.items.map((e) => (
                      <tr key={e.id} className="border-b border-[#F1F5F9]">
                        <td className="py-1.5">{e.date}</td>
                        <td className="py-1.5">{e.paid_by}</td>
                        <td className="py-1.5">{e.category}</td>
                        <td className="py-1.5 max-w-xs truncate">{e.description}</td>
                        <td className="py-1.5 text-right font-medium">${e.amount.toFixed(2)}</td>
                        <td className="py-1.5">{e.payment_method}</td>
                        <td className="py-1.5 text-center">{e.receipt_attached ? 'Yes' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {reportData.period_type === 'annual' && reportData.expenses.top_expenses?.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 border-b border-[#0A0A0A] pb-2">Top Expenses</h4>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Date</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Paid By</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Category</th>
                    <th className="text-left py-2 font-semibold text-[#4B5563]">Description</th>
                    <th className="text-right py-2 font-semibold text-[#4B5563]">Amount</th>
                  </tr></thead>
                  <tbody>
                    {reportData.expenses.top_expenses.map((e) => (
                      <tr key={e.id} className="border-b border-[#F1F5F9]">
                        <td className="py-1.5">{e.date}</td>
                        <td className="py-1.5">{e.paid_by}</td>
                        <td className="py-1.5">{e.category}</td>
                        <td className="py-1.5 max-w-xs truncate">{e.description}</td>
                        <td className="py-1.5 text-right font-medium">${e.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="border-t-2 border-[#0A0A0A] pt-4 mt-8 text-center text-xs text-[#9CA3AF]">
              <p className="font-medium text-[#4B5563]">{reportData.organization}</p>
              <p>This report was generated for internal use only. For questions, contact the finance administrator.</p>
              <p className="mt-1">Report generated: {new Date(reportData.generated_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {!reportData && !reportLoading && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-16 text-center">
          <BarChart3 className="w-12 h-12 text-[#E5E7EB] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">Generate a Report</h3>
          <p className="text-sm text-[#4B5563]">Select a report type and period above, then click Generate.</p>
          <p className="text-xs text-[#9CA3AF] mt-2">Reports can be printed or saved as PDF for board meetings, grant reporting, and IRS documentation.</p>
        </div>
      )}
    </div>
  );
};

export default FinanceReportsTab;
